import React, { useEffect, useState } from 'react';
import { Box, Button, Chip, Typography, CircularProgress } from '@mui/material';
import ResponsiveTable from '../common/ResponsiveTable';
import { Column } from '../common/responsiveTable.types';
import { useNavigate } from 'react-router-dom';
import { ExpenseClaimService } from '../../services/expense-claim.service';
import { ExpenseClaimDTO } from '../../interfaces/expense.interface';
import { CLAIM_STATUS_COLOR, claimRef, claimStatusLabel, formatAmount } from '../../utils/expenseDisplay';
import { formatDate } from '../../utils/dateUtils';
import { useToast } from '../../context/ToastContext';

const MyExpenseClaims: React.FC = () => {
    const [claims, setClaims] = useState<ExpenseClaimDTO[]>([]);
    const [loading, setLoading] = useState(true);
    const toast = useToast();
    const navigate = useNavigate();

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const load = async () => {
        setLoading(true);
        try {
            const page = await ExpenseClaimService.listMine(0, 50);
            setClaims(page.content);
        } catch (e: any) {
            toast.showApiError(e, 'Failed to load expense claims');
        } finally {
            setLoading(false);
        }
    };

    const columns: Column<ExpenseClaimDTO>[] = [
        { key: 'ref', header: 'Claim', priority: 1, helpKey: 'expense.myClaims.col.claim', render: c => claimRef(c.id) },
        { key: 'title', header: 'Title', priority: 1, helpKey: 'expense.myClaims.col.title', render: c => c.title },
        { key: 'period', header: 'Period', helpKey: 'expense.myClaims.col.period', render: c => `${c.periodFrom ? formatDate(c.periodFrom) : '—'} → ${c.periodTo ? formatDate(c.periodTo) : '—'}` },
        { key: 'total', header: 'Total', align: 'right', priority: 1, helpKey: 'expense.myClaims.col.total', render: c => formatAmount(c.totalAmount) },
        { key: 'status', header: 'Status', helpKey: 'expense.myClaims.col.status', render: c => <Chip size="small" data-help="expense.myClaims.col.status" label={claimStatusLabel(c.status)} color={CLAIM_STATUS_COLOR[c.status]} /> },
        { key: 'lines', header: 'Lines', align: 'center', helpKey: 'expense.myClaims.col.lines', render: c => c.lines?.length ?? 0 },
        { key: 'actions', header: 'Actions', helpKey: 'expense.myClaims.col.actions', align: 'right', role: 'actions', render: c => (
            <Button size="small" data-help="expense.myClaims.action.view" onClick={() => navigate(`/expense/my-claims/${c.id}`)}>View</Button>
        ) },
    ];

    return (
        <Box sx={{ p: { xs: 1.5, sm: 3 } }} data-help-page="expense.myClaims.page">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="h5">My Expense Claims</Typography>
                <Button variant="contained" data-help="expense.myClaims.new" onClick={() => navigate('/expense/my-claims/new')}>
                    New Claim
                </Button>
            </Box>
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
            ) : (
                <ResponsiveTable<ExpenseClaimDTO> data-help-section="expense.myClaims.table"
                    pageKey="expense.myClaims"
                    columns={columns}
                    rows={claims}
                    getRowKey={c => c.id}
                    emptyMessage='No claims yet. Click "New Claim" to start.'
                />
            )}
        </Box>
    );
};

export default MyExpenseClaims;
